import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  ArrowRight, 
  BarChart3, 
  HelpCircle, 
  MessageCircle,
  Eye,
  Copy,
  Search,
  TrendingUp,
  Loader2,
  RefreshCw,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import './HelpAnalyticsDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FAQ ID to readable label mapping
const FAQ_LABELS = {
  'account': { en: 'Create Account', ar: 'إنشاء حساب' },
  'post-ad': { en: 'Post an Ad', ar: 'نشر إعلان' },
  'contact-seller': { en: 'Contact Seller', ar: 'التواصل مع البائع' },
  'delete-account': { en: 'Delete Account', ar: 'حذف الحساب' },
  'report': { en: 'Report Violation', ar: 'الإبلاغ عن مخالفة' },
};

function HelpAnalyticsDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack('/account');
  const { token, user } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('7');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/account');
      return;
    }
    fetchAnalytics();
  }, [user, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/analytics/help-dashboard?days=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.message);
      toast.error(t('had.failedToLoadAnalytics'));
    } finally {
      setLoading(false);
    }
  };

  const getFaqLabel = (faqId) => {
    const label = FAQ_LABELS[faqId];
    if (label) {
      return isRTL ? label.ar : label.en;
    }
    return faqId;
  };

  const getContactLabel = (type) => {
    const labels = {
      'email': t('cont.email'),
      'whatsapp': t('cont.whatsapp'),
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="help-analytics-page" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="loading-state">
          <Loader2 size={32} className="animate-spin" />
          <p>{t('had.loadingAnalytics')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="help-analytics-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="help-analytics-dashboard">
      {/* Header */}
      <header className="analytics-header">
        <button className="back-btn" onClick={goBack} data-testid="back-btn">
          <BackIcon size={20} />
        </button>
        <h1 className="header-title">
          <BarChart3 size={24} />
          <span>{t('had.helpSupportAnalytics')}</span>
        </h1>
        <button className="refresh-btn" onClick={fetchAnalytics} data-testid="refresh-btn">
          <RefreshCw size={20} />
        </button>
      </header>

      {/* Period Selector */}
      <div className="period-selector">
        <Calendar size={18} />
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="period-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t('had.last7Days')}</SelectItem>
            <SelectItem value="14">{t('had.last14Days')}</SelectItem>
            <SelectItem value="30">{t('had.last30Days')}</SelectItem>
            <SelectItem value="90">{t('had.last90Days')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-btn">
            {t('had.retry')}
          </button>
        </div>
      ) : data && (
        <>
          {/* Funnel Overview */}
          <section className="analytics-section">
            <h2 className="section-title">
              <TrendingUp size={20} />
              <span>{t('had.userFunnel')}</span>
            </h2>
            <div className="funnel-cards">
              <div className="funnel-card" data-testid="funnel-views">
                <div className="funnel-icon blue">
                  <Eye size={24} />
                </div>
                <div className="funnel-info">
                  <span className="funnel-value">{data.funnel?.page_views || 0}</span>
                  <span className="funnel-label">{t('had.pageViews')}</span>
                </div>
              </div>

              <div className="funnel-arrow">→</div>

              <div className="funnel-card" data-testid="funnel-faq">
                <div className="funnel-icon purple">
                  <HelpCircle size={24} />
                </div>
                <div className="funnel-info">
                  <span className="funnel-value">{data.funnel?.faq_engagement || 0}</span>
                  <span className="funnel-label">{t('had.faqOpens')}</span>
                  <span className="funnel-rate">{data.funnel?.faq_rate || 0}%</span>
                </div>
              </div>

              <div className="funnel-arrow">→</div>

              <div className="funnel-card" data-testid="funnel-contact">
                <div className="funnel-icon green">
                  <MessageCircle size={24} />
                </div>
                <div className="funnel-info">
                  <span className="funnel-value">{data.funnel?.contact_attempts || 0}</span>
                  <span className="funnel-label">{t('had.contactAttempts')}</span>
                  <span className="funnel-rate">{data.funnel?.contact_rate || 0}%</span>
                </div>
              </div>
            </div>
          </section>

          {/* Event Breakdown */}
          <section className="analytics-section">
            <h2 className="section-title">
              <BarChart3 size={20} />
              <span>{t('had.eventBreakdown')}</span>
            </h2>
            <div className="event-grid">
              {Object.entries(data.event_stats || {}).map(([eventName, stats]) => {
                const iconMap = {
                  'help_view': Eye,
                  'faq_open': HelpCircle,
                  'contact_click': MessageCircle,
                  'copy_click': Copy,
                  'search_help': Search,
                };
                const Icon = iconMap[eventName] || Eye;
                const labelMap = {
                  'help_view': t('had.pageViews'),
                  'faq_open': t('had.faqOpens'),
                  'contact_click': t('had.contactClicks'),
                  'copy_click': t('had.copyClicks'),
                  'search_help': t('had.helpSearches'),
                };
                return (
                  <div key={eventName} className="event-card" data-testid={`event-${eventName}`}>
                    <Icon size={20} className="event-icon" />
                    <div className="event-info">
                      <span className="event-label">{labelMap[eventName] || eventName}</span>
                      <span className="event-value">{stats.total_count}</span>
                      <span className="event-sessions">
                        {stats.unique_sessions} {t('had.uniqueSessions')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Top FAQs */}
          {data.top_faqs && data.top_faqs.length > 0 && (
            <section className="analytics-section">
              <h2 className="section-title">
                <HelpCircle size={20} />
                <span>{t('had.topFaqs')}</span>
              </h2>
              <div className="list-card">
                {data.top_faqs.map((faq, idx) => (
                  <div key={faq.faq_id} className="list-item" data-testid={`top-faq-${idx}`}>
                    <span className="list-rank">#{idx + 1}</span>
                    <span className="list-label">{getFaqLabel(faq.faq_id)}</span>
                    <span className="list-value">{faq.count} {t('unitOpens')}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Top Contact Methods */}
          {data.top_contact_methods && data.top_contact_methods.length > 0 && (
            <section className="analytics-section">
              <h2 className="section-title">
                <MessageCircle size={20} />
                <span>{t('had.topContactMethods')}</span>
              </h2>
              <div className="list-card">
                {data.top_contact_methods.map((method, idx) => (
                  <div key={method.type} className="list-item" data-testid={`top-contact-${idx}`}>
                    <span className="list-rank">#{idx + 1}</span>
                    <span className="list-label">{getContactLabel(method.type)}</span>
                    <span className="list-value">{method.count} {t('unitClicks')}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {(!data.event_stats || Object.keys(data.event_stats).length === 0) && (
            <div className="empty-state">
              <BarChart3 size={48} />
              <h3>{t('had.noDataYet')}</h3>
              <p>{t('had.dataWillAppearHereWhen')}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HelpAnalyticsDashboard;
